import { DEMO_CALLS } from '@/landing/content'
import { ContentPage, contentMeta, type ContentPageData } from '@/site/content'

const sample = DEMO_CALLS.find((c) => c.key === 'after-hours')!

const page: ContentPageData = {
  path: '/funktsii/robota-24-7',
  parent: { name: 'Можливості', path: '/funktsii' },
  crumb: 'Робота 24/7',
  title: 'AI-агент 24/7: дзвінки вночі й у вихідні | Anytel',
  description:
    'Anytel відповідає на дзвінки цілодобово: вночі, у вихідні, у свята й у години пік. Як налаштувати агента лише на неробочий час або на всі дзвінки.',
  eyebrow: 'Можливості',
  h1: 'Робота 24/7 і в неробочий час',
  lead:
    'Клієнти дзвонять тоді, коли зручно їм, а не коли працює ваш офіс. Агент відповідає вночі, у вихідні, у свята і тоді, коли всі лінії зайняті, а зранку на вас чекають заявки, а не пропущені.',
  sections: [
    {
      h2: 'Коли дзвінки лишаються без відповіді',
      blocks: [
        {
          list: [
            'Вечір і ніч: люди повертаються з роботи і нарешті мають час подзвонити.',
            'Вихідні та свята: офіс зачинено, а потреба в послузі нікуди не ділась.',
            'Обідня перерва і початок дня, поки адміністратор ще в дорозі.',
            'Години пік: усі лінії зайняті, а клієнт не хоче чекати на гудках.',
          ],
        },
        'Детальніше про вечірні дзвінки і як на них відповідати в статті [«Як не втрачати дзвінки після 18:00»](/blog/yak-ne-vtrachaty-dzvinky-pislya-18).',
      ],
    },
    {
      h2: 'Три способи налаштувати',
      blocks: [
        { h3: 'Агент на всі дзвінки' },
        'Шаблон «AI-адміністратор 24/7»: агент бере кожен дзвінок, а людям передає лише те, що потребує людини.',
        { h3: 'Вдень люди, вночі агент' },
        'Шаблон «Вдень оператор, вночі AI»: у робочі години дзвінок іде вашим співробітникам, після закриття і у вихідні агенту. Робочі години й вихідні задаються в налаштуваннях.',
        { h3: 'Агент як підстраховка' },
        'Дзвінок спершу йде співробітникам, і лише якщо ніхто не відповів, його приймає агент. Так дзвінок не лишається без відповіді, навіть коли всі зайняті.',
      ],
    },
    {
      h2: 'Що агент робить уночі',
      blocks: [
        'Те саме, що й удень: відповідає на питання, записує в календар на вільний час, приймає заявки. Якщо питання може вирішити лише людина, агент обіцяє передзвін у робочий час, а система сама передзвонить клієнту зранку: у кабінеті є черга передзвонів з кількома спробами.',
        'Записи з ночі з’являються в календарі, заявки в кабінеті й [у Telegram](/funktsii/zayavky-v-telegram). Зранку команда починає день з готового списку, а не з прослуховування автовідповідача.',
      ],
    },
  ],
  dialog: {
    h2: 'Приклад: дзвінок о 22:40',
    context: sample.context,
    dialog: sample.dialog,
    result: sample.result,
  },
  after: [
    {
      h2: 'Скільки дзвінків приходить після закриття',
      blocks: [
        'Більшість бізнесів цього не знає, бо неробочі дзвінки просто ніде не рахуються. Після запуску агента відповідь з’являється сама: на огляді кабінету видно дзвінки по днях, а в журналі видно, о котрій вони прийшли і чим закінчились. Якщо хочете оцінити це ще до запуску, скористайтеся нашою [інструкцією на тиждень](/blog/skilky-dzvinkiv-vy-propuskayete).',
      ],
    },
    {
      h2: 'Свята і особливі дні',
      blocks: [
        'У календарях можна наперед позначити вихідні й скорочені дні. Агент не запише клієнта на день, коли ви не працюєте, і назве найближчий робочий.',
        'Логіку робочих годин, меню й переведень можна змінити будь-коли в [конструкторі сценаріїв](/funktsii/ivr-menyu).',
      ],
    },
  ],
  faq: [
    {
      q: 'Чи можна вмикати агента лише на вечір і вихідні?',
      a: 'Так, це шаблон «Вдень оператор, вночі AI». Робочі години задаються один раз, сценарій сам перемикає дзвінки.',
    },
    {
      q: 'Чи дорожчий нічний дзвінок?',
      a: 'Ні. Оплачуються хвилини розмови з агентом за тарифом, без нічних надбавок. Тарифи на сторінці [цін](/tsiny).',
    },
    {
      q: 'Чи можна, щоб уночі агент лише приймав заявки, без запису в календар?',
      a: 'Так. Що робить агент, ви визначаєте в його інструкціях і в сценарії: можна дозволити лише відповіді на питання й заявки, а записи підтверджувати зранку.',
    },
    {
      q: 'Що, якщо вночі дзвонить клієнт з терміновою справою?',
      a: 'Сценарій може переводити такі дзвінки на черговий номер співробітника. Наприклад, якщо клієнт натиснув «1, терміново» в голосовому меню.',
    },
  ],
  related: ['/funktsii/ivr-menyu', '/funktsii/ai-agent-dlya-dzvinkiv', '/dlya/avtoservisu', '/blog/yak-ne-vtrachaty-dzvinky-pislya-18'],
}

export const meta = contentMeta(page)

export default function Page() {
  return <ContentPage page={page} />
}
